import { useState } from "react";
import {
  AlertTriangle,
  AlertCircle,
  Check,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Minus,
  Plus,
} from "lucide-react";
import { FindingRow } from "@/components/experience/finding-row";
import type { FindingType, Severity } from "@/components/finding-badges";
import { cn } from "@/lib/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";
import {
  type DeltaGroupKey,
  type DeltaItem,
  type RunVerification,
  splitRunVerification,
} from "@/lib/run-findings";

export type { RunVerification, DeltaItem };

interface GroupConfig {
  key: DeltaGroupKey;
  label: string;
  note?: string;
  icon: React.ComponentType<{ className?: string }>;
  chipClass: string;
}

const GROUPS: GroupConfig[] = [
  {
    key: "regressed",
    label: "Regressed",
    note: "verified earlier, broken again",
    icon: AlertTriangle,
    chipClass: "text-destructive border-destructive/30 bg-destructive/10",
  },
  {
    key: "new_problem",
    label: "New problems",
    icon: Plus,
    chipClass: "text-amber-500 border-amber-500/30 bg-amber-500/10",
  },
  {
    key: "persists_problem",
    label: "Persisting",
    icon: AlertCircle,
    chipClass: "text-muted-foreground border-border bg-muted/60",
  },
  {
    key: "resolved",
    label: "Resolved",
    note: "re-checked in this run",
    icon: Check,
    chipClass: "text-emerald-500 border-emerald-500/30 bg-emerald-500/10",
  },
  {
    key: "new_strength",
    label: "New strengths",
    icon: Plus,
    chipClass: "text-emerald-500 border-emerald-500/30 bg-emerald-500/10",
  },
  {
    key: "persists_strength",
    label: "Retained strengths",
    icon: Check,
    chipClass: "text-muted-foreground border-border bg-muted/40",
  },
  {
    key: "not_observed",
    label: "Not seen this run",
    note: "not evidence of a fix",
    icon: Minus,
    chipClass: "text-muted-foreground border-border bg-muted/40",
  },
  {
    key: "uncertain",
    label: "Uncertain",
    icon: HelpCircle,
    chipClass: "text-muted-foreground border-border bg-muted/40",
  },
];

export interface SinceLastRunProps {
  verification: RunVerification;
  projectId: string;
  onSelectFinding?: (targetId: string, item: DeltaItem) => void;
}

/** The since-last-run delta ribbon of a local run. */
export function SinceLastRun({
  verification,
  projectId,
  onSelectFinding,
}: SinceLastRunProps) {
  const byKey = splitRunVerification(verification);
  const hasRegressions = (byKey.regressed?.length ?? 0) > 0;
  const [expanded, setExpanded] = useState(hasRegressions);
  const [selectedGroup, setSelectedGroup] = useState<DeltaGroupKey | "all">(
    "all",
  );

  const groups = GROUPS.map((g) => ({ ...g, items: byKey[g.key] })).filter(
    (g) => g.items.length > 0,
  );
  if (groups.length === 0) return null;

  const totalCount = groups.reduce((acc, g) => acc + g.items.length, 0);

  const handleGroupClick = (groupKey: DeltaGroupKey) => {
    if (!expanded) {
      setExpanded(true);
      setSelectedGroup(groupKey);
    } else if (selectedGroup === groupKey) {
      setSelectedGroup("all");
    } else {
      setSelectedGroup(groupKey);
    }
  };

  const handleAllClick = () => {
    if (!expanded) {
      setExpanded(true);
    }
    setSelectedGroup("all");
  };

  const visibleGroups =
    selectedGroup === "all"
      ? groups
      : groups.filter((g) => g.key === selectedGroup);

  return (
    <section
      className="rounded-md border border-border/70 bg-muted/20 text-xs overflow-hidden"
      aria-label="Run delta comparison"
    >
      {/* ── Delta Ribbon Bar ── */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-3.5 py-2">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className="font-mono text-xs font-semibold text-muted-foreground shrink-0">
            Run delta
          </span>
          <span className="text-border/70 select-none">|</span>
          <div className="flex flex-wrap items-center gap-1.5">
            <SimpleTooltip content="Show all changes from the baseline run">
              <button
                type="button"
                onClick={handleAllClick}
                aria-pressed={expanded && selectedGroup === "all"}
                className={cn(
                  "inline-flex items-center gap-1 whitespace-nowrap rounded-sm border px-2 py-0.5 text-xs font-semibold tabular-nums transition-all cursor-pointer",
                  expanded && selectedGroup === "all"
                    ? "bg-foreground text-background border-foreground shadow-xs"
                    : "text-muted-foreground border-border/70 bg-muted/40 hover:bg-muted/70 hover:text-foreground",
                )}
              >
                <span>All · {totalCount}</span>
              </button>
            </SimpleTooltip>

            {groups.map((g) => {
              const Icon = g.icon;
              const isSelected = expanded && selectedGroup === g.key;
              const isDimmed =
                expanded && selectedGroup !== "all" && selectedGroup !== g.key;

              return (
                <SimpleTooltip
                  key={g.key}
                  content={
                    g.note
                      ? `${g.label}: ${g.note} (Click to filter)`
                      : `${g.label} (Click to filter)`
                  }
                >
                  <button
                    type="button"
                    onClick={() => handleGroupClick(g.key)}
                    aria-pressed={isSelected}
                    className={cn(
                      "inline-flex items-center gap-1 whitespace-nowrap rounded-sm border px-1.5 py-0.5 text-xs font-semibold tabular-nums transition-all cursor-pointer",
                      g.chipClass,
                      isSelected &&
                        "ring-2 ring-primary ring-offset-1 ring-offset-background font-bold shadow-xs opacity-100",
                      isDimmed && "opacity-45 hover:opacity-90",
                    )}
                  >
                    <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
                    <span>
                      {g.label} · {g.items.length}
                    </span>
                  </button>
                </SimpleTooltip>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors shrink-0 cursor-pointer ml-auto"
          aria-expanded={expanded}
        >
          <span>{expanded ? "Hide diff" : `Inspect diff (${totalCount})`}</span>
          {expanded ? (
            <ChevronUp className="h-3 w-3" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-3 w-3" aria-hidden="true" />
          )}
        </button>
      </div>

      {/* ── Inspection Details Drawer ── */}
      {expanded && (
        <div className="border-t border-border/60 bg-background/50 p-3 sm:p-4 space-y-3.5">
          {selectedGroup !== "all" && (
            <div className="flex items-center justify-between text-xs text-muted-foreground pb-0.5">
              <span>
                Showing only{" "}
                <strong className="text-foreground font-semibold">
                  {visibleGroups[0]?.label}
                </strong>{" "}
                ({visibleGroups[0]?.items.length}{" "}
                {visibleGroups[0]?.items.length === 1 ? "item" : "items"})
              </span>
              <button
                type="button"
                onClick={() => setSelectedGroup("all")}
                className="text-xs font-medium text-primary hover:underline cursor-pointer"
              >
                Show all categories ({totalCount})
              </button>
            </div>
          )}

          {visibleGroups.map((g) => {
            const Icon = g.icon;
            return (
              <div key={g.key} className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 whitespace-nowrap rounded-sm border px-1.5 py-0.5 text-xs font-semibold tabular-nums",
                      g.chipClass,
                    )}
                  >
                    <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
                    <span>
                      {g.label} · {g.items.length}
                    </span>
                  </span>
                  {g.note && (
                    <span className="text-xs text-muted-foreground">
                      {g.note}
                    </span>
                  )}
                </div>

                <ul className="divide-y divide-border/40 rounded border border-border/50 bg-card/60 overflow-hidden">
                  {g.items.map((item) => {
                    const target = item.canonical_id ?? item.id;
                    const isStrengthGroup =
                      g.key === "new_strength" || g.key === "persists_strength";
                    const findingType: FindingType = item.type
                      ? (item.type as FindingType)
                      : isStrengthGroup
                        ? "helps"
                        : "hinders";
                    const findingSeverity: Severity | null =
                      g.key === "regressed" ? "critical" : null;

                    if (!target) {
                      return (
                        <li
                          key={`${g.key}-${item.title}`}
                          className="px-3 py-1.5"
                        >
                          <FindingRow
                            variant="compact"
                            finding={{
                              id: item.title,
                              title: item.title,
                              description: "",
                              type: findingType,
                              severity: findingSeverity,
                            }}
                          />
                        </li>
                      );
                    }

                    const href = `/projects/${projectId}/findings/${target}`;

                    return (
                      <li
                        key={`${g.key}-${target}`}
                        className="px-3 py-1.5 transition-colors hover:bg-muted/40"
                      >
                        <FindingRow
                          variant="compact"
                          href={href}
                          onSelect={
                            onSelectFinding
                              ? () => onSelectFinding(target, item)
                              : undefined
                          }
                          finding={{
                            id: target,
                            title: item.title,
                            description: "",
                            type: findingType,
                            severity: findingSeverity,
                          }}
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
